import React, { useEffect, useRef, useState } from 'react';
// CSS compartilhado dos modais de filtro (nasceu no Contac e já serve 5 modais por import).
// Reuso, nunca cópia — o que for exclusivo deste modal fica no `.css` ao lado.
import '../../../components/modal/contac/filtro/ModalFiltro.css';
import '../../../shared/ui/modal-close-button.css';
import { getDefaultPeriodo } from '../components/PeriodoHeader';

interface FiltrosModalProps {
  /** O que está APLICADO hoje — o modal reabre mostrando isto, nunca em branco (BUG-V43). */
  periodoAplicado: string;
  onAplicar: (periodo: string) => void;
  onFechar: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Filtros da lista de Comissionamentos (16/09/2026, a pedido do Victor).
 *
 * Segue o padrão do projeto: aplicação **em bloco** (o estado é local e só sai em "Aplicar
 * Filtros", então N ajustes geram UMA requisição, e "Cancelar" desfaz de verdade) e sem
 * resumo dos filtros na tela — quem quiser conferir reabre o modal.
 *
 * ⚠️ Montagem CONDICIONAL (`{aberto && <FiltrosModal .../>}`), sem prop `isOpen`: é o que
 * garante que o `useState` inicial seja recalculado a cada abertura. O desenho antigo do
 * Contac (modal sempre montado + efeito que recopia as props) deixou rascunho descartado
 * sobreviver para a abertura seguinte na at-61.
 *
 * ⚠️ Não reusa o `ModalFiltro` do Contac por incompatibilidade real: aquele é de INTERVALO
 * (início/fim) e carrega a janela consultável do Contac (defasagem de 2 meses, ano ≥ 2025),
 * que não vale aqui — Comissionamentos filtra UMA competência. O que se reusa é o CSS.
 */
const FiltrosModal: React.FC<FiltrosModalProps> = ({
  periodoAplicado,
  onAplicar,
  onFechar,
  sidebarRef,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [periodo, setPeriodo] = useState<string>(periodoAplicado);

  // Fecha ao clicar fora (do modal E da sidebar) e no Escape, como os demais modais.
  useEffect(() => {
    const aoClicarFora = (e: MouseEvent) => {
      const alvo = e.target as Node;
      const foraDoModal = modalRef.current && !modalRef.current.contains(alvo);
      const foraDaSidebar = !sidebarRef?.current || !sidebarRef.current.contains(alvo);
      if (foraDoModal && foraDaSidebar) onFechar();
    };
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar();
    };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [onFechar, sidebarRef]);

  const [mm, yyyy] = periodo.split('-');

  const aplicar = () => {
    onAplicar(periodo);
    onFechar();
  };

  return (
    <div className="modal-filtro-overlay">
      <div className="modal-filtro-content" ref={modalRef}>
        <div className="filtro-modal-card">
          <div className="filtro-modal-header">
            <div>
              <h2 className="filtro-modal-title">Filtros</h2>
              <p className="filtro-modal-subtitle">
                Escolha a competência dos dados exibidos na lista
              </p>
            </div>
            <button className="close-button" onClick={onFechar} aria-label="Fechar">
              &times;
            </button>
          </div>

          <div className="filtro-modal-body">
            <div className="filtro-section">
              <h3 className="filtro-section-title">Competência</h3>
              <div className="filtro-field">
                <label htmlFor="filtro-periodo">Período (mm/aaaa)</label>
                <input
                  id="filtro-periodo"
                  type="month"
                  className="premium-text-input"
                  value={`${yyyy}-${mm}`}
                  onChange={(e) => {
                    const [y, m] = e.target.value.split('-');
                    if (!y || !m) return;
                    setPeriodo(`${m}-${y}`);
                  }}
                />
              </div>
              <p className="filtro-modal-subtitle" style={{ marginTop: 8 }}>
                Os valores de contratos, regras e pagamentos são sempre os desta competência.
              </p>
            </div>
          </div>

          <div className="filtro-modal-footer">
            <button
              type="button"
              className="filtro-btn-secondary"
              onClick={() => setPeriodo(getDefaultPeriodo())}
            >
              Limpar filtros
            </button>
            <button type="button" className="filtro-btn-secondary" onClick={onFechar}>
              Cancelar
            </button>
            <button type="button" className="filtro-btn-primary" onClick={aplicar}>
              Aplicar Filtros
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiltrosModal;
